'use client';

import React, { useState } from 'react';
import { X, MapPin, Heart, Star } from 'lucide-react';
import Image from 'next/image';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const slides = [
  {
    icon: <MapPin className="w-8 h-8 text-white" />,
    iconBg: 'bg-green-600',
    title: 'Willkommen bei WINEWOOD! 🎉',
    text: 'Hier findest du schnell und übersichtlich Freizeitaktivitäten und coole Locations für Ausflüge in deiner Nähe. Schön, dass du dabei bist!',
  },
  {
    icon: <Heart className="w-8 h-8 text-white" />,
    iconBg: 'bg-rose-500',
    title: 'Entdecken & Favoriten',
    text: 'Du kannst dir Favoriten speichern oder einfach unter Entdecken sehen, was da wo du bist in deiner Nähe ist. Immer das Beste direkt bei dir.',
  },
  {
    icon: <Star className="w-8 h-8 text-white" />,
    iconBg: 'bg-amber-500',
    title: 'WINEWOOD+ Abo',
    text: 'Mit einem W+ Abo bekommst du Rabatte bei vielen Angeboten und Partnern. Einfach vor Ort die App vorzeigen und – je nach Angebot – schon bei einem Partner den Preis wieder raus haben.',
  },
];

export default function OnboardingModal({ isOpen, onClose }: OnboardingModalProps) {
  const [step, setStep] = useState(0);

  if (!isOpen) return null;

  const current = slides[step];
  const isLast = step === slides.length - 1;

  const handleNext = () => {
    if (isLast) {
      onClose();
    } else {
      setStep((s) => s + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm mx-4 mb-6 sm:mb-0 bg-white rounded-3xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Schließen"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>

        {/* Logo strip */}
        <div className="flex justify-center pt-6 pb-2">
          <Image
            src="/assets/images/logo_grun_fur_app-1787922846254.png"
            alt="WINEWOOD Logo"
            width={120}
            height={36}
            className="object-contain h-8 w-auto"
          />
        </div>

        {/* Content */}
        <div className="px-7 pt-4 pb-6 text-center">
          {/* Icon */}
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${current.iconBg} mb-5 shadow-lg`}>
            {current.icon}
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
            {current.title}
          </h2>

          {/* Text */}
          <p className="text-sm text-gray-600 leading-relaxed mb-7">
            {current.text}
          </p>

          {/* Step dots */}
          <div className="flex justify-center gap-2 mb-6">
            {slides.map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === step ? 'w-6 bg-green-600' : 'w-2 bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={handleNext}
            className="w-full py-3.5 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-colors shadow-md"
          >
            {isLast ? 'Los geht\'s!' : 'Weiter'}
          </button>

          {/* Skip */}
          {!isLast && (
            <button
              onClick={onClose}
              className="mt-3 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Überspringen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
